"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const client_library_1 = require("@seneca/client-library");
const UserInfoBodyNotValidError_1 = __importDefault(require("./errors/UserInfoBodyNotValidError"));
const UserInfoDoesNotExistError_1 = __importDefault(require("./errors/UserInfoDoesNotExistError"));
const UserAlreadyExistsError_1 = __importDefault(require("./errors/UserAlreadyExistsError"));
const UserPreferencesOperations_1 = require("./UserPreferencesOperations");
const validations_1 = require("./validations");
/**
 * UserServiceClient
 * @param {any} fetch an object implementing the fetch api (for browsers this can be fetch.bind() for node require('node-fetch'))
 * @param {Object} options options object input data and dependency injection
 * @param {Object} options.fetch change fetch method from whatwg-fetch (optional, default: whatwg-fetch)
 * @param {string} options.protocol the http protocol of the service endpoint
 * @param {string} options.host the host of the service endpoint
 * @param {string} options.path the path of the service endpoint (optional)
 * @param {number} options.retries the number of times the client should retry on network error / service error (optional, default: 3)
 * @param {number} options.delayInSecs the delay between retries (optional, default: 2)
 * @param {Object} options.credentials identifiers and secrets required to hit protected endpoints (optional)
 * @param {string} options.region states the region of the service for signing (optional, default: eu-west-1)
 * @param {RequestMaker} options.requestMaker for overriding request making logic (optional, default: RequestMaker)
 * @throws {UrlConstructionError} when either options.protocol or options.host is missing
 */
class UserServiceClient extends client_library_1.Client {
    constructor(fetch, options = {}) {
        super(fetch, options);
    }
    async _makeCommonInfoRequest(baseUrl, method, path, body) {
        let url = this._constructUrl(baseUrl);
        if (path) {
            url += `/${path}`;
        }
        const expectedResponses = [200, 201, 204, 400, 404, 409, 422];
        const options = { method };
        if (body) {
            options.body = JSON.stringify(body);
        }
        const response = await this.requestMaker.makeRequest(url, options, expectedResponses);
        if (response.status === 422) {
            const body = response.json;
            throw new UserInfoBodyNotValidError_1.default(body.pointer, body.reason);
        }
        if (response.status === 400) {
            throw new client_library_1.InvalidParamError('type', body.type, 'invalid');
        }
        if (response.status === 404) {
            throw new UserInfoDoesNotExistError_1.default('userId', path);
        }
        if (response.status === 409) {
            throw new UserAlreadyExistsError_1.default();
        }
        if (response.status === 200 || response.status === 201) {
            const body = response.json;
            return body;
        }
        // else 204 no exception or response body
        return;
    }
    async _makeInfoRequest(method, path, body) {
        return this._makeCommonInfoRequest(`api/user-info`, method, path, body);
    }
    async _makeAdminRequest(method, path, body) {
        return this._makeCommonInfoRequest(`api/admin`, method, path, body);
    }
    validateParameter(param, name) {
        if (!param) {
            throw new client_library_1.MissingParamError(name);
        }
    }
    validateSignupAttributes(attributes) {
        if (!attributes || Object.keys(attributes).length === 0) {
            throw new client_library_1.MissingParamError('attributes');
        }
        if (!attributes.email) {
            throw new client_library_1.MissingParamError('email');
        }
        (0, validations_1.validateAttributes)(attributes);
    }
    validateUpdateAttributes(attributes) {
        if (!attributes || Object.keys(attributes).length === 0) {
            throw new client_library_1.MissingParamError('attributes');
        }
        if (!!attributes.email) {
            throw new client_library_1.InvalidParamError('email', attributes.email, "Email can't be updated");
        }
        (0, validations_1.validateAttributes)(attributes);
    }
    /**
     * Signs a new user up to the info service
     * @param {Object} attributes user attributes
     * @param {string} attributes.userId the subscription id from cognito
     * @param {string} options.email users email
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<UserInfo>} the user info object
     */
    async saveMyInfo(attributes) {
        this.validateParameter(attributes, 'attributes');
        this.validateSignupAttributes(attributes);
        const sanitizedEmail = attributes.email.toLocaleLowerCase();
        return await this._makeInfoRequest('POST', 'me', Object.assign(Object.assign({}, attributes), { email: sanitizedEmail }));
    }
    /**
     * Creates a new user to the info service
     * @param {Object} user user attributes
     * @param {string} user.userId firebase user id
     * @param {string} user.email user email
     * @param {Object} preferences user preferences attributes
     * @param {string} region user region
     * @returns {Promise.<{user: UserInfoI; preferences: Preferences | undefined;}>} request response
     */
    async adminCreateUser({ user, preferences, region }) {
        var _a;
        const sanitizedEmail = (_a = user === null || user === void 0 ? void 0 : user.email) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase();
        return this._makeAdminRequest('POST', 'user', {
            data: {
                user: Object.assign(Object.assign({}, user), { email: sanitizedEmail }),
                preferences,
                region
            }
        });
    }
    /**
     * Updates user info
     * @param {UserInfoUpdateObject} user user update object, accepts null fields
     * @param {string} region user region
     * @returns {Promise.<{user: UserInfoI;}>} request response
     */
    async adminUpdateUser({ user, region }) {
        const { email, guardianEmail } = user;
        if (email)
            user.email = email.toLocaleLowerCase();
        if (guardianEmail)
            user.guardianEmail = guardianEmail.toLocaleLowerCase();
        return this._makeAdminRequest('PATCH', 'user', { user, region });
    }
    /**
     * Updates users info
     * @param {UserInfoUpdateObject[]} users user update objects, accept null fields
     * @param {string} region users region
     * @returns {Promise.<{users: UserInfoI[]; errors: UserUpdateError[];}>} request response
     */
    async adminUpdateUsers({ users, region }) {
        const usersWithSanitizedEmail = users.map(user => {
            const { email, guardianEmail } = user;
            if (email)
                user.email = email.toLocaleLowerCase();
            if (guardianEmail)
                user.guardianEmail = guardianEmail.toLocaleLowerCase();
            return user;
        });
        return this._makeAdminRequest('PATCH', 'users', {
            users: usersWithSanitizedEmail,
            region
        });
    }
    /**
     * Updates user attributes for authenticated user
     * @param {array<{Name: string, Value: string}>} attributeList
     * @throws {MissingAttributeError} if no updates are sent
     * @throws {UserNotAuthenticatedError} if an unauthed user attempts to call
     * @throws {InvalidParameterError} if it encouters an attribute that is not recognised
     * @throws {InvalidParameterError} if attempt to update email is made
     * @throws {UnexpectedServerResponse} when server ouput anything other than success
     * @returns {Promise.<void>}
     */
    async updateAttributes(attributes) {
        this.validateParameter(attributes, 'attributes');
        this.validateUpdateAttributes(attributes);
        return await this._makeInfoRequest('POST', 'me/update', attributes);
    }
    /**
     * Swap email endpoint for authentication service
     * @param originalUserId
     * @param newUserId
     * @throws {UnexpectedServerResponse}
     * @returns {Promise.<void>}
     */
    async swapEmail(originalUserId, newUserId) {
        this.validateParameter(originalUserId, 'originalUserId');
        this.validateParameter(newUserId, 'newUserId');
        return await this._makeAdminRequest('POST', 'swap', {
            originalUserId,
            newUserId
        });
    }
    /**
     * Update email endpoint for authenticationService
     * @param newEmail
     * @param password
     * @throws {MissingAttributeError} if no updates are sent
     * @throws {UserNotAuthenticatedError} if an unauthed user attempts to call
     * @throws {InvalidParameterError} if email not send
     * @throws {UnexpectedServerResponse} when server ouput anything other than success
     * @returns {Promise.<void>}
     */
    async updateMyEmail(newEmail) {
        this.validateParameter(newEmail, 'email');
        const sanitizedNewEmail = newEmail.toLocaleLowerCase();
        return await this._makeInfoRequest('POST', 'email', {
            email: sanitizedNewEmail
        });
    }
    /**
     * Fetches the user info for the current user
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<UserInfo>} the user info object
     */
    async fetchMyInfo() {
        return await this._makeInfoRequest('GET', 'me', null);
    }
    async fetchUserByEmail(email) {
        this.validateParameter(email, 'email');
        const url = this._constructUrl('api/admin/user-info/email');
        const expectedResponses = [200, 400, 404];
        const response = await this.requestMaker.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify({ email })
        }, expectedResponses);
        if (response.status === 400) {
            throw new client_library_1.MissingParamError('email');
        }
        if (response.status === 404) {
            throw new UserInfoDoesNotExistError_1.default('email', email);
        }
        if (response.status === 200) {
            const body = response.json;
            return body;
        }
        return response;
    }
    /**
     * Generate a new display name for the current user
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<UserInfo>} the user info object
     */
    async createNewDisplayName() {
        return await this._makeInfoRequest('POST', 'me/update', {
            displayName: '!generate'
        });
    }
    /**
     * Batch fetches user info for a list of users
     * @param {array<userIds>} - the list of userIds to fetch info for
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.array<UserInfo>} the array of user info objects
     */
    async fetchBatchUserInfo(userIds) {
        if (!userIds) {
            throw new client_library_1.MissingParamError('userIds');
        }
        return await this._makeInfoRequest('POST', 'batch/get', { userIds });
    }
    async _makePreferenceRequest(method, body) {
        const url = this._constructUrl('api/user-prefs/me');
        const options = {
            method
        };
        if (body) {
            options.body = JSON.stringify(body);
        }
        const response = await this.requestMaker.makeRequest(url, options);
        if (response.status === 200) {
            return response.json;
        }
    }
    /**
     * Fetches the user's preferences
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<UserPreferences>} the preferences object
     */
    async fetchMyPreferences() {
        return await this._makePreferenceRequest('GET');
    }
    /**
     * Replaces the user's preferences
     * @param {preferences} - object containing the preferences to be replaced.
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<UserPreferences>} the preferences object
     */
    async replaceMyPreferences(preferences) {
        if (!preferences) {
            throw new client_library_1.MissingParamError('preferences');
        }
        const prefsToUpdate = {};
        Object.keys(preferences).forEach(pref => {
            prefsToUpdate[pref] = {
                operation: UserPreferencesOperations_1.UserPreferencesOperations.REPLACE,
                value: preferences[pref]
            };
        });
        return await this._makePreferenceRequest('POST', {
            preferences: prefsToUpdate
        });
    }
    /**
     * Increments the user's preferences
     * @param {array<preferences>} - array containing the keys of the preferences to be incremented
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<UserPreferences>} the preferences object
     */
    async incrementMyPreferences(preferences) {
        if (!preferences) {
            throw new client_library_1.MissingParamError('preferences');
        }
        if (typeof preferences === 'string') {
            preferences = [preferences];
        }
        const prefsToUpdate = {};
        preferences.forEach((pref) => {
            prefsToUpdate[pref] = {
                operation: UserPreferencesOperations_1.UserPreferencesOperations.INCREMENT
            };
        });
        return await this._makePreferenceRequest('POST', {
            preferences: prefsToUpdate
        });
    }
    /**
     * Deletes the user's preferences
     * @param {array<preferences>} - array containing the keys of the preferences to be deleted
     * @throws {UnexpectedServerResponse} when service returns an unexpected response
     * @throws {RequestLimitExceeded} when the max number of retries has been hit
     * @returns {Promise.<UserPreferences>} the preferences object
     */
    async deleteMyPreferences(preferences) {
        if (!preferences) {
            throw new client_library_1.MissingParamError('preferences');
        }
        if (typeof preferences === 'string') {
            preferences = [preferences];
        }
        const prefsToUpdate = {};
        preferences.forEach((pref) => {
            prefsToUpdate[pref] = {
                operation: UserPreferencesOperations_1.UserPreferencesOperations.DELETE
            };
        });
        return await this._makePreferenceRequest('POST', {
            preferences: prefsToUpdate
        });
    }
    _constructUrl(path) {
        let url = this.url;
        if (!this.url.endsWith('/')) {
            url += '/';
        }
        url += path;
        return url;
    }
}
exports.default = UserServiceClient;
//# sourceMappingURL=UserServiceClient.js.map